import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter } from 'react-feather';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Droplet, 
  MoreVertical, 
  MapPin, 
  Calendar, 
  User, 
  Phone,
  AlertCircle,
  Home,
  Loader,
  Navigation,
  Users,
  Check,
  X,
  ThumbsUp,
  ThumbsDown,
  Image
} from 'react-feather';
import CreateRequest from './CreateRequest';
import DirectionsMap from '../../Maps/DirectionsMap';
import { useNavigate } from 'react-router-dom';

// Fallback data for active requests
const dummyActiveRequests = [
  {
    id: 'REQ001',
    bloodGroup: 'A+',
    units: 2,
    urgencyLevel: 'Critical',
    status: 'Pending',
    patientName: 'John Doe',
    patientPhone: '+91 98765-43210',
    State: 'Maharashtra',
    City:'mumbai',
    Location: 'mumbai',
    createdAt: new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    createdAtDate: new Date(),
    donors: []
  }
];

const ActiveRequest = () => {
  const { user } = useAuth();
  const [activeRequests, setActiveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDirections, setShowDirections] = useState(false);
  const [showDonationConfirmationModal, setShowDonationConfirmationModal] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [donationFeedback, setDonationFeedback] = useState('');
  const [updatingDonationStatus, setUpdatingDonationStatus] = useState(false);
  const [rejectionType, setRejectionType] = useState('complete'); // 'complete' or 'reupload'
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const navigate = useNavigate();

  // Get current location when component mounts
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Fetch active requests when component mounts or user changes
  useEffect(() => {
    if (user) {
      fetchActiveRequests();
    } else {
      setActiveRequests(dummyActiveRequests);
      setLoading(false);
    }
  }, [user]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
        },
        error => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your current location. Some features may be limited.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  };

  const fetchActiveRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Fetch active requests from API
      const response = await api.get('/requests/me');
      console.log('Active requests:', response.data);
      
      // Process the data to include donation confirmations
      const processedRequests = response.data.map(request => {
        // Check if any donors have confirmed their donation
        const confirmedDonors = request.donors ? request.donors.filter(donor => 
          donor.donationProofPhoto && donor.status !== 'cancelled'
        ) : [];
        
        // Count accepted donors
        const acceptedDonors = request.donors ? request.donors.filter(donor => 
          donor.status === 'accepted' || donor.status === 'donated' || donor.status === 'pending_confirmation'
        ) : [];
        
        // Format the creation date
        const createdDate = new Date(request.createdAt);
        const formattedDate = createdDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        
        const formattedTime = createdDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });
        
        return {
          id: request.id || request._id, // Ensure id is always available
          ...request,
          confirmedDonors,
          acceptedDonors,
          acceptedDonorsCount: request.acceptedDonorsCount || acceptedDonors.length,
          maxDonorsReached: request.maxDonorsReached || acceptedDonors.length >= 3,
          formattedDate,
          formattedTime
        };
      });
      
      // Sort requests with the latest ones at the top
      const sortedRequests = processedRequests.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Sort in descending order (newest first)
      });
      
      setActiveRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching active requests:', error);
      setError('Failed to load your active requests. Please try again.');
      toast.error('Failed to load your active requests');
      // Use dummy data as fallback
      setActiveRequests(dummyActiveRequests);
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequestSuccess = (newRequest) => {
    setActiveRequests(prev => [newRequest, ...prev]);
    setShowNewRequestForm(false);
    toast.success('Blood request created successfully!');
  };

  const handleViewDirections = (request) => {
    setSelectedRequest(request);
    setShowDirections(true);
  };

  const closeDirectionsModal = () => {
    setShowDirections(false);
    setSelectedRequest(null);
  };

  // Open donation confirmation modal
  const openDonationConfirmationModal = (request, donor) => {
    console.log('Opening donation confirmation modal with donor:', donor);
    
    // Extract the donor ID correctly, handling all possible formats
    const donorId = typeof donor.donor === 'object' ? donor.donor._id : 
                   (donor.donor || donor.donorId || donor._id);
                   
    console.log('Extracted donorId:', donorId);
    
    setSelectedRequest(request);
    setSelectedDonation({
      donorId: donorId,
      donorName: donor.donorName || 'Donor',
      donorEmail: donor.donorEmail || 'Not provided',
      donorPhone: donor.donorPhone || 'Not provided',
      acceptedDate: donor.acceptedDate ? new Date(donor.acceptedDate).toLocaleDateString() : 'Not recorded',
      donationProofPhoto: donor.donationProofPhoto || '',
      notes: donor.notes || '',
      donationDate: donor.donationDate || new Date().toISOString()
    });
    setDonationFeedback('');
    setShowDonationConfirmationModal(true);
  };

  // Close donation confirmation modal
  const closeDonationConfirmationModal = () => {
    setShowDonationConfirmationModal(false);
    setSelectedRequest(null);
    setSelectedDonation(null);
    setDonationFeedback('');
  };

  // Update donation status (confirm or reject)
  const updateDonationStatus = async (status) => {
    if (!selectedRequest || !selectedDonation) return;
    
    setUpdatingDonationStatus(true);
    
    try {
      const requestId = selectedRequest.id || selectedRequest._id;
      console.log('Updating donation status with:', {
        requestId,
        donorId: selectedDonation.donorId,
        donationStatus: status,
        feedback: donationFeedback
      });
      
      const response = await api.put(`/requests/${requestId}/donation-status`, {
        donorId: selectedDonation.donorId,
        donationStatus: status,
        feedback: donationFeedback
      });
      
      console.log('Donation status update response:', response.data);
      
      // Close the modal first before updating state to prevent errors during navigation
      closeDonationConfirmationModal();
      
      // Show success message
      if (status === 'confirmed') {
        toast.success(
          <div className="text-center">
            <div className="font-bold text-lg mb-1">Donation Verified Successfully!</div>
            <div>The request has been marked as fulfilled and other donors have been notified.</div>
          </div>
        );
      } else {
        toast.success(
          status === 'confirmed' 
            ? 'Donation confirmed successfully!' 
            : status === 'reupload'
              ? 'Reupload requested successfully!'
              : 'Donation rejected successfully!'
        );
      }
      
      // Refresh the requests to update the UI
      fetchActiveRequests();
    } catch (error) {
      console.error('Error updating donation status:', error);
      setUpdatingDonationStatus(false);
      
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        return;
      }
      
      toast.error(error.response?.data?.message || 'Failed to update donation status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle donation confirmation with confirmation dialog
  const handleConfirmDonation = () => {
    setPendingAction('confirm');
    setShowConfirmationDialog(true);
  };
  
  // Handle donation rejection with options
  const handleRejectDonation = (type) => {
    setRejectionType(type);
    setPendingAction('reject');
    setShowConfirmationDialog(true);
  };
  
  // Execute the pending action after confirmation
  const executePendingAction = () => {
    if (pendingAction === 'confirm') {
      updateDonationStatus('confirmed');
    } else if (pendingAction === 'reject') {
      updateDonationStatus(rejectionType === 'complete' ? 'rejected' : 'reupload');
    }
    setShowConfirmationDialog(false);
    setPendingAction(null);
  };
  
  // Cancel the pending action
  const cancelPendingAction = () => {
    setShowConfirmationDialog(false);
    setPendingAction(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'fulfilled':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'fulfilled':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Fulfilled</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pending</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const filteredRequests = activeRequests.filter(request => {
    if (selectedStatus === 'all') return true;
    return request.status.toLowerCase() === selectedStatus.toLowerCase();
  });

  const requestCounts = {
    all: activeRequests.length,
    fulfilled: activeRequests.filter(r => r.status.toLowerCase() === 'fulfilled').length,
    pending: activeRequests.filter(r => r.status.toLowerCase() === 'pending').length
  };

  return (
    <div className="space-y-6">
      {/* Header with Create Request Button */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Active Requests</h2>
            <p className="text-gray-500 mt-1">Manage your active blood donation requests</p>
          </div>
          <button 
            onClick={() => setShowNewRequestForm(true)}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter size={16} />
            Filter
            <span className="w-2 h-2 transition-transform duration-200" 
              style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              ▼
            </span>
          </button>
        </div>

        {/* Filter Options */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setSelectedStatus('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      selectedStatus === 'all'
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    All ({requestCounts.all})
                  </button>
                  <button
                    onClick={() => setSelectedStatus('fulfilled')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      selectedStatus === 'fulfilled'
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Fulfilled ({requestCounts.fulfilled})
                  </button>
                  <button
                    onClick={() => setSelectedStatus('pending')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      selectedStatus === 'pending'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Pending ({requestCounts.pending})
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-gray-200 border-t-red-500 rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your active requests...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Something went wrong</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
            <button 
              onClick={fetchActiveRequests}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Active Requests Grid */}
        {!loading && !error && filteredRequests.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
            {filteredRequests.map((request) => (
              <motion.div
                key={request.id || request._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                {/* Status Banner */}
                <div className={`px-4 py-3 ${
                  request.status === 'Pending' ? 'bg-yellow-50' :
                  request.status === 'Active' ? 'bg-blue-50' :
                  request.status === 'Fulfilled' ? 'bg-green-50' :
                  'bg-gray-50'
                } flex flex-wrap justify-between items-center`}>
                  <div className="flex items-center space-x-2 mb-1 sm:mb-0">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      request.status === 'Pending' ? 'bg-yellow-500' :
                      request.status === 'Active' ? 'bg-blue-500' :
                      request.status === 'Fulfilled' ? 'bg-green-500' :
                      'bg-gray-500'
                    }`}></span>
                    <span className="font-medium text-sm">{request.status}</span>
                    <span className="text-xs text-gray-500 ml-2">ID: {(request.id || request._id || '').toString().substring(0, 8)}</span>
                  </div>
                  
                  {/* Donor Count Indicator */}
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm font-medium">
                      {request.acceptedDonorsCount || 0}/3 Donors
                      {request.maxDonorsReached && (
                        <span className="ml-1 text-green-600">(Max reached)</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Request Content */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Blood Group Icon */}
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Droplet className="w-6 h-6 text-red-600" />
                    </div>

                    {/* Request Details */}
                    <div className="flex-grow">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">Blood Group {request.bloodGroup}</h3>
                          <p className="text-sm text-gray-500">Patient: {request.patientName}</p>
                        </div>
                        <div className="bg-red-50 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 flex items-center">
                          {request.unitsNeeded || request.units} {(request.unitsNeeded || request.units) > 1 ? 'units' : 'unit'}
                        </div>
                      </div>

                      {/* Request Details */}
                      <div className="mt-4 space-y-3">
                        {/* Created Date and Time */}
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {request.formattedDate || formatDate(request.createdAt)}, {request.formattedTime || ''}
                          </span>
                        </div>
                        
                        {/* Urgency Level */}
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-gray-600" />
                          <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                            request.urgencyLevel === 'Critical' ? 'bg-red-100 text-red-700' :
                            request.urgencyLevel === 'High' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {request.urgencyLevel || 'Normal'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{request.Location || (request.hospital && request.hospital.name) || 'Location not specified'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span className="text-sm">{request.patientPhone || (request.contactInfo && request.contactInfo.phone) || 'Phone not available'}</span>
                        </div>
                      </div>

                      {/* Donors Section */}
                      {request.donors && request.donors.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium text-gray-900 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Donors ({request.donors.length})
                          </h4>
                          <div className="mt-2 space-y-3">
                            {request.donors.map((donor, index) => (
                              <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center gap-3 mb-2 sm:mb-0">
                                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-gray-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {donor.donorName || (donor.donor && typeof donor.donor === 'object' ? donor.donor.name : 'Donor')}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <Phone className="w-3 h-3" />
                                      <a href={`tel:${donor.donorPhone || (donor.donor && donor.donor.phone)}`} className="hover:text-red-600">
                                        {donor.donorPhone || (donor.donor && donor.donor.phone) || 'No phone'}
                                      </a>
                                      <span className="px-1.5 py-0.5 rounded-full bg-gray-200">
                                        {donor.status === 'accepted' ? 'Accepted' : 
                                         donor.status === 'donated' ? 'Donated' : 
                                         donor.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                                      </span>
                                    </div>
                                    {donor.acceptedDate && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        <Calendar className="w-3 h-3 inline mr-1" />
                                        <span>Accepted: {new Date(donor.acceptedDate).toLocaleDateString('en-IN', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Show verification button if donor has uploaded proof */}
                                {donor.donationProofPhoto && donor.status !== 'donated' && donor.status !== 'rejected' && (
                                  <button
                                    onClick={() => openDonationConfirmationModal(request, donor)}
                                    className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start mt-2 sm:mt-0"
                                  >
                                    <Image className="w-3 h-3" />
                                    Verify Donation
                                  </button>
                                )}
                                
                                {/* Show verified badge if donation is confirmed */}
                                {donor.status === 'donated' && (
                                  <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start mt-2 sm:mt-0">
                                    <Check className="w-3 h-3" />
                                    Verified
                                  </span>
                                )}

                                {/* Show rejected badge if donation is rejected */}
                                {donor.status === 'rejected' && (
                                  <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start mt-2 sm:mt-0">
                                    <X className="w-3 h-3" />
                                    Rejected
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => handleViewDirections(request)}
                      className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Navigation className="w-4 h-4" />
                      <span className="whitespace-nowrap">View Directions</span>
                    </button>
                    <button 
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      onClick={() => {
                        const phoneNumber = request.patientPhone || (request.contactInfo && request.contactInfo.phone);
                        if (phoneNumber) {
                          window.open(`tel:${phoneNumber.replace(/\D/g, '')}`);
                        } else {
                          toast.error('Phone number not available');
                        }
                      }}
                    >
                      <Phone className="w-4 h-4" />
                      <span className="whitespace-nowrap">Call Patient</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredRequests.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl shadow-sm p-8 text-center mb-20"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Droplet className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">No Active Requests</h3>
            <p className="mt-2 text-sm text-gray-500">
              You don't have any active blood donation requests at the moment.
            </p>
            <button 
              onClick={() => setShowNewRequestForm(true)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              Create New Request
            </button>
          </motion.div>
        )}

        {/* Create Request Modal */}
        {showNewRequestForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <CreateRequest 
                onClose={() => setShowNewRequestForm(false)} 
                onSuccess={handleNewRequestSuccess}
                currentLocation={currentLocation}
              />
            </motion.div>
          </div>
        )}

        {/* Directions Modal */}
        {showDirections && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Directions to Hospital</h3>
                  <button 
                    onClick={closeDirectionsModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="h-[60vh]">
                  {currentLocation ? (
                    <DirectionsMap 
                      origin={currentLocation}
                      destination={selectedRequest.Location}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Loader className="w-8 h-8 text-gray-400 mx-auto animate-spin" />
                        <p className="mt-4 text-gray-600">Getting your current location...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Donation Confirmation Modal */}
        {showDonationConfirmationModal && selectedRequest && selectedDonation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <h3 className="text-xl font-bold text-gray-900">Verify Blood Donation</h3>
                <p className="text-gray-500 mt-1">Please confirm if you received this blood donation</p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Donation Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Donation Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Blood Type</p>
                      <p className="font-medium text-gray-900">{selectedRequest.bloodType || selectedRequest.bloodGroup}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hospital</p>
                      <p className="font-medium text-gray-900">{selectedRequest.hospital ? selectedRequest.hospital.name : 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Donation Date</p>
                      <p className="font-medium text-gray-900">{formatDate(selectedDonation.donationDate)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Donor Information */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Donor Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{selectedDonation.donorName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{selectedDonation.donorEmail}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{selectedDonation.donorPhone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Request Accepted On</p>
                      <p className="font-medium text-gray-900">{selectedDonation.acceptedDate}</p>
                    </div>
                  </div>
                </div>
                
                {/* Donation Photo */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Donation Proof Photo</h4>
                  {selectedDonation.donationProofPhoto ? (
                    <div className="border rounded-lg overflow-hidden">
                      <img 
                        src={selectedDonation.donationProofPhoto} 
                        alt="Donation Proof" 
                        className="w-full h-auto max-h-[300px] object-contain"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-8 text-center">
                      <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No photo provided</p>
                    </div>
                  )}
                </div>
                
                {/* Donor's Notes */}
                {selectedDonation.notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Donor's Notes</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 italic">"{selectedDonation.notes}"</p>
                    </div>
                  </div>
                )}
                
                {/* Feedback */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Your Feedback (Optional)</h4>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows="3"
                    placeholder="Add your feedback or thank you note for the donor..."
                    value={donationFeedback}
                    onChange={(e) => setDonationFeedback(e.target.value)}
                  ></textarea>
                </div>
              </div>
              
              <div className="p-6 border-t">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={closeDonationConfirmationModal}
                    className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDonation}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Confirm Donation
                  </button>
                  <button
                    onClick={() => handleRejectDonation('complete')}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 sm:col-span-1"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Reject
                  </button>
                  <button
                    onClick={() => handleRejectDonation('reupload')}
                    className="w-full px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 sm:col-span-1"
                  >
                    <Image className="w-4 h-4" />
                    Request Reupload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmationDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-lg max-w-md w-full p-6"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {pendingAction === 'confirm' ? 'Confirm Donation' : 
                 rejectionType === 'complete' ? 'Reject Donation' : 'Request Image Reupload'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                {pendingAction === 'confirm' 
                  ? 'Are you sure you want to confirm this donation? This action cannot be undone.'
                  : rejectionType === 'complete'
                    ? 'Are you sure you want to reject this donation? This action cannot be undone.'
                    : 'Are you sure you want to request the donor to reupload a different image? The donor will be notified.'}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelPendingAction}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                
                <button
                  onClick={executePendingAction}
                  className={`flex-1 py-2 text-white rounded-lg transition-colors ${
                    pendingAction === 'confirm' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : rejectionType === 'complete'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-yellow-500 hover:bg-yellow-600'
                  }`}
                >
                  {pendingAction === 'confirm' ? 'Yes, Confirm' : 
                   rejectionType === 'complete' ? 'Yes, Reject' : 'Yes, Request Reupload'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveRequest;
